import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PaymentGatewayCard from '../components/PaymentGatewayCard';
import PaymentGatewayConfig from '../components/PaymentGatewayConfig';
import { usePaymentGateways } from '../hooks/usePaymentGateways';

const { 
  FiSettings, FiCreditCard, FiGlobe, FiShield, FiBell, FiUser, 
  FiMail, FiLock, FiRefreshCw, FiPlus, FiExternalLink 
} = FiIcons;

const Settings = () => {
  const [activeTab, setActiveTab] = useState('payment-gateways');
  const [showGatewayConfig, setShowGatewayConfig] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);

  const { gateways, loading, error, updateGateway, testConnection } = usePaymentGateways();

  const handleConfigureGateway = (gateway) => {
    setSelectedGateway(gateway);
    setShowGatewayConfig(true);
  };

  const handleSaveGateway = async (gatewayId, config) => {
    await updateGateway(gatewayId, config);
    setShowGatewayConfig(false);
    setSelectedGateway(null);
  };

  const tabs = [
    { id: 'payment-gateways', label: 'Payment Gateways', icon: FiCreditCard },
    { id: 'general', label: 'General Settings', icon: FiSettings },
    { id: 'security', label: 'Security', icon: FiShield },
    { id: 'notifications', label: 'Notifications', icon: FiBell },
    { id: 'profile', label: 'Profile', icon: FiUser }
  ];

  const renderPaymentGatewaysTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-800">Payment Gateway Integration</h3>
          <p className="text-gray-600 mt-1">
            Configure payment gateways to accept online payments from your clients
          </p>
        </div>
        <button className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200">
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Gateway</span>
        </button>
      </div>

      {/* Payment Gateway Overview */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">🚀 Payment Gateway Features</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4">
            <h5 className="font-medium text-gray-800 mb-2">💳 Stripe Integration</h5>
            <p className="text-sm text-gray-600">Global payments with credit cards, bank transfers, and digital wallets</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h5 className="font-medium text-gray-800 mb-2">🇮🇳 Razorpay Integration</h5>
            <p className="text-sm text-gray-600">Indian payments via UPI, net banking, cards, and wallets</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h5 className="font-medium text-gray-800 mb-2">🔗 Payment Links</h5>
            <p className="text-sm text-gray-600">Generate secure payment links for invoices and subscriptions</p>
          </div>
        </div>
      </div>

      {/* Gateway Cards */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <SafeIcon icon={FiRefreshCw} className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading payment gateways...</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error} (Running in demo mode)</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gateways.map((gateway, index) => (
            <motion.div
              key={gateway.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <PaymentGatewayCard
                gateway={gateway}
                onUpdate={updateGateway}
                onTest={testConnection}
                onConfigure={() => handleConfigureGateway(gateway)}
              />
            </motion.div>
          ))}
        </div>
      )}

      {/* Payment Links Section */}
      <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <SafeIcon icon={FiExternalLink} className="w-5 h-5 mr-2 text-green-600" />
          Payment Link Configuration
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="font-medium text-gray-800 mb-2">Invoice Payment Links</h5>
            <p className="text-sm text-gray-600 mb-4">
              Automatically generate secure payment links for invoices that clients can use to pay online.
            </p>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Auto-generate payment links</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Include payment link in invoice emails</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Send payment reminders</span>
              </label>
            </div>
          </div>
          
          <div>
            <h5 className="font-medium text-gray-800 mb-2">Subscription Payments</h5>
            <p className="text-sm text-gray-600 mb-4">
              Enable recurring payments for subscription-based services with automatic billing.
            </p>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Enable subscription billing</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Auto-retry failed payments</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Proration for plan changes</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-gray-800">Payment Link Expiry</h5>
              <p className="text-sm text-gray-600">Set default expiry time for payment links</p>
            </div>
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="24">24 hours</option>
              <option value="72">3 days</option>
              <option value="168">1 week</option>
              <option value="720">30 days</option>
              <option value="never">Never expire</option>
            </select>
          </div>
        </div>
      </div>

      {/* Webhook Configuration */}
      <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <SafeIcon icon={FiGlobe} className="w-5 h-5 mr-2 text-purple-600" />
          Webhook Configuration
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Webhook Endpoint URL
            </label>
            <input
              type="url"
              defaultValue="https://yourdomain.com/webhooks/payment"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://yourdomain.com/webhooks/payment"
            />
            <p className="text-xs text-gray-500 mt-1">
              This endpoint will receive payment notifications from your gateways
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h6 className="font-medium text-gray-800 mb-2">Stripe Webhook Events</h6>
              <div className="space-y-1">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">payment_intent.succeeded</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">payment_intent.payment_failed</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">invoice.payment_succeeded</span>
                </label>
              </div>
            </div>
            
            <div>
              <h6 className="font-medium text-gray-800 mb-2">Razorpay Webhook Events</h6>
              <div className="space-y-1">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">payment.captured</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">payment.failed</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">subscription.charged</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">General Settings</h3>
        <p className="text-gray-600">Configure general application settings and preferences.</p>
        
        <div className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <input
              type="text"
              defaultValue="Digital Marketing Agency Pvt Ltd"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Zone
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="UTC+5:30">Asia/Kolkata (UTC+5:30)</option>
              <option value="UTC">UTC</option>
              <option value="UTC-5">Eastern Time (UTC-5)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Currency
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="INR">Indian Rupee (INR)</option>
              <option value="USD">US Dollar (USD)</option>
              <option value="EUR">Euro (EUR)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Security Settings</h3>
        <p className="text-gray-600">Manage your account security and access controls.</p>
        
        <div className="mt-6 space-y-4">
          <div>
            <h4 className="font-medium text-gray-800 mb-2">Two-Factor Authentication</h4>
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <p className="text-sm text-gray-700">Add an extra layer of security to your account</p>
              </div>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors duration-200">
                Enable 2FA
              </button>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-800 mb-2">API Access</h4>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Enable API access</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Require API key for access</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Notification Preferences</h3>
        <p className="text-gray-600">Choose what notifications you want to receive.</p>
        
        <div className="mt-6 space-y-6">
          <div>
            <h4 className="font-medium text-gray-800 mb-3">Email Notifications</h4>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Payment received</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Invoice overdue</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">New client signup</span>
              </label>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-800 mb-3">Push Notifications</h4>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Payment alerts</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm text-gray-700">System updates</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Information</h3>
        <p className="text-gray-600">Update your personal and business information.</p>
        
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                defaultValue="John"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                defaultValue="Doe"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              defaultValue="john@agency.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              defaultValue="+1 (555) 123-4567"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Settings</h1>
          <p className="text-gray-600">
            Configure your application preferences and payment gateways
          </p>
        </div>
      </motion.div>

      {/* Navigation Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-soft border border-gray-200"
      >
        <div className="flex flex-wrap border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {activeTab === 'payment-gateways' && renderPaymentGatewaysTab()}
        {activeTab === 'general' && renderGeneralTab()}
        {activeTab === 'security' && renderSecurityTab()}
        {activeTab === 'notifications' && renderNotificationsTab()}
        {activeTab === 'profile' && renderProfileTab()}
      </motion.div>

      {/* Payment Gateway Configuration Modal */}
      <PaymentGatewayConfig
        isOpen={showGatewayConfig}
        onClose={() => setShowGatewayConfig(false)}
        gateway={selectedGateway}
        onSave={handleSaveGateway}
      />
    </div>
  );
};

export default Settings;